<script lang="ts" setup>
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import type { PageSeo } from '@pkg/sanity-utils/types/seo';
import { THEME } from '@pkg/config/misc/theme';
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
import { CONTENT_CHOICE } from '@pkg/sanity-utils/constants/contentChoice';
import type { AuthorLocalised } from '@pkg/sanity-utils/types/author';
import type { ModularBlock } from '@pkg/sanity-utils/types/modularContentBlocks';
import ModularBlocksRenderer from '@pkg/modular-blocks/frontend/ModularBlocksRenderer.vue';
import SubHeroSection from '@pkg/modular-blocks/blocks/outer/SubHeroSection/SubHeroSection.vue';
import Section from '@pkg/modular-blocks/blocks/outer/Section/Section.vue';

import Page404 from '~/app/features/cms/components/Page404.vue';
import { useLocalisedDate } from '~/app/composables/useLocalisedDate';

import { useSanityPageSeoMeta } from '~/app/composables/useSanityPageSeoMeta';
import SocialsShareGroup from '~/app/components/blocks/SocialsShareGroup.vue';
import PostAuthor from '~/app/features/blog/components/PostAuthor.vue';
import { fixYoutubeIframes } from '~/utils/fixYoutubeIframes';
import { usePostBreadcrumbJsonLd } from '~/app/composables/usePostBreadcrumbJsonLd';
import { usePressNewsArticleJsonLd } from '~/app/composables/usePressNewsArticleJsonLd';
import { useBlogPostingJsonLd } from '~/app/composables/useBlogPostingJsonLd';

const props = defineProps<{
  content?: {
    _type: string
    _createdAt: string;
    _updatedAt: string;
    publishedAt?: string;
    updatedAt?: string;
    excerpt?: string;
    author: AuthorLocalised;
    content: Array<ModularBlock>
    migrated?: string;
    pageContent: Array<ModularBlock>
    defaultPageBlocks?: Array<ModularBlock>
    pageVisibility: string
    seoSocial: PageSeo
    slug: string
    language: LOCALE_CODE,
    _translations: Array<{ title: string; slug: string; language: LOCALE_CODE }>
    [key: string]: any
  }
}>();

const { t } = useI18n();

const pageModularBlocks = props.content?.contentChoice === CONTENT_CHOICE.CUSTOM
  ? props.content?.pageContent
  : props.content?.defaultPageBlocks;

if (props.content?.seoSocial) {
  useSanityPageSeoMeta(props.content.seoSocial);
}

usePostBreadcrumbJsonLd(computed(() => props.content));
usePressNewsArticleJsonLd(computed(() => props.content));
useBlogPostingJsonLd(computed(() => props.content));

// @todo this needs to be changed to a published date when we have one on the post
const publishedAt = props.content?.publishedAt ? useLocalisedDate(props.content.publishedAt) : undefined;
// @todo this needs to be changed to an updated at field, rather than the internal updatedAt, so the CMS user can edit it
const updatedAt = props.content?.updatedAt ? useLocalisedDate(props.content.updatedAt) : undefined;

const postedAndUpdatedText = updatedAt && publishedAt !== updatedAt
  ? t('blogPostedUpdated', { postedDate: publishedAt, updatedDate: updatedAt })
  : t('blogPosted', { postedDate: publishedAt });

// Fix YouTube iframes in migrated content to prevent 513 errors
const fixedMigratedContent = computed(() => {
  if (!props.content?.migrated) { return ''; }
  return fixYoutubeIframes(props.content.migrated);
});

useHead({ bodyAttrs: { class: `first-block-theme--${THEME.DARK}` } });

</script>

<template>
  <template v-if="props.content">
    <div v-if="props.content" class="c-post">
      <SubHeroSection
        :theme="THEME.DARK"
        media-type="image"
        :variant="props.content.image ? 'mediaCenter' : 'mediaNone'"
        :image="props.content.image"
      >
        <template #content>
          <BaseTitle
            variant="center"
            :pre-title="props.content.pretitle"
            :title="props.content.title"
            size="large"
          />
          <PostAuthor
            v-if="props.content?.author"
            :author="props.content.author"
            class="mt-[24px]"
          />

          <span v-if="publishedAt && postedAndUpdatedText" class="c-post__date mt-16 tablet-wide:mt-24">
            {{ postedAndUpdatedText }}
          </span>
        </template>
      </SubHeroSection>

      <div class="c-post__content" :data-theme="THEME.LIGHT">
        <Section :theme="THEME.LIGHT">
          <div class="wrapper wrapper--content">
            <PortableEditor v-if="props.content.content" :content="props.content.content" />
            <div v-else-if="fixedMigratedContent" v-dompurify-html="fixedMigratedContent" class="c-post__content--migrated" />
          </div>
          <div class="pt-72 pb-32">
            <SocialsShareGroup />
          </div>
        </Section>
      </div>
    </div>

    <div>
      <ModularBlocksRenderer v-if="pageModularBlocks" :blocks="pageModularBlocks" type="outer" />
    </div>
  </template>

  <Page404 v-else />
</template>

<style scoped>
@import '~/assets/css/mixins/_mixins.index.pcss';
@import '~/assets/css/generics/_generics.html.pcss';

.c-post {
  --post-content-spacing: theme('spacing.40');

  @screen tablet-wide {
    --post-content-spacing: theme('spacing.72');
  }
}

.c-post__content {
  --post-content-image-width: 1010px;
  padding-top: theme('spacing.40');

  @screen tablet-wide {
    padding-top: theme('spacing.72');
  }

  :deep(.c-section) {
    padding-top: 0;
  }

  .c-portable-editor > {
    p:first-of-type,
    :deep(.block__image) {
      margin-top: var(--post-content-spacing) !important;
      margin-bottom: var(--post-content-spacing) !important;
    }
  }

  :deep(.block__image) {
    margin-left: calc(var(--wrapper-padding) * -1);
    margin-right: calc(var(--wrapper-padding) * -1);

    @screen tablet-wide {
      margin-left: calc((var(--post-content-image-width) - var(--wrapper-content-max-width)) / 2 * -1);
      margin-right: calc((var(--post-content-image-width) - var(--wrapper-content-max-width)) / 2 * -1);
    }
  }
}

.c-post__date {
  @mixin body-small;
  color: var(--theme-tertiary-text-color)
}

:deep(.c-cta-section__bg--top) {
  background-color: var(--theme-background-color-light);
}

.c-post__content--migrated {
  :deep(iframe) {
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
  }

  :deep(.medium-insert-embeds),
  :deep(.video),
  :deep(.video-youtube-nocookie) {
    display: flex;
    justify-content: center;
  }
}
</style>
